import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';

const styles = theme => ({
  draggable: {
    position: 'relative',
  },
  draggableDragged: {
    zIndex: 10,
  },
  draggableContentWithTransition: {
    backgroundColor: theme.palette.common.white,
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.short,
    }),
  },
  draggableHandle: {
    position: 'relative',
  },
});

const withSortableListItem = (WrappedComponent) => {
  class SortableListItem extends Component {
    constructor(props) {
      super(props);

      this.state = {
        dragged: false,
        position: null,
        bounds: null,
      };

      this.onDraggableDataChange = this.onDraggableDataChange.bind(this);
      this.onStartDrag = this.onStartDrag.bind(this);
      this.onDrag = this.onDrag.bind(this);
      this.onStopDrag = this.onStopDrag.bind(this);
    }

    componentDidMount() {
      this.draggableDataChangeTimeout = setTimeout(() => {
        this.onDraggableDataChange();
      }, 100);
    }

    componentDidUpdate(prevProps) {
      const { timestamp } = this.props;
      if (timestamp !== prevProps.timestamp) {
        this.draggableDataChangeTimeout = setTimeout(() => {
          this.onDraggableDataChange();
        }, 100);
      }
    }

    componentWillUnmount() {
      clearTimeout(this.draggableDataChangeTimeout);
    }

    onDraggableDataChange() {
      const { boundsSelector, index, onDraggableDataChange } = this.props;
      if (boundsSelector) {
        const draggableRefDOMRect = this.draggableRef.getBoundingClientRect();
        const boundsDOMRect = document.querySelector(boundsSelector).getBoundingClientRect();
        const left = Math.round(boundsDOMRect.left - draggableRefDOMRect.left);
        const top = Math.round(boundsDOMRect.top - draggableRefDOMRect.top);
        const right = Math.round((boundsDOMRect.width - draggableRefDOMRect.width) + left);
        const bottom = Math.round((boundsDOMRect.height - draggableRefDOMRect.height) + top);
        const data = {
          bounds: { left, top, right, bottom },
          width: draggableRefDOMRect.width,
          height: draggableRefDOMRect.height,
        };
        const draggableData = {};
        draggableData[index] = data;
        this.setState(data);
        onDraggableDataChange(draggableData);
      }
    }

    onStartDrag(event, data) {
      const { onStartDrag, index } = this.props;
      const { bounds, width, height } = this.state;
      this.onDraggableDataChange();
      this.setState({ dragged: true });
      if (onStartDrag) {
        onStartDrag({
          x: data.x - bounds.left,
          y: data.y - bounds.top,
          width,
          height,
          defaultX: -bounds.left,
          defaultY: -bounds.top,
          index,
        });
      }
    }

    onDrag(event, data) {
      const { onDrag, index } = this.props;
      const { bounds, width, height } = this.state;
      if (onDrag) {
        onDrag({
          x: -bounds.left,
          y: data.y - bounds.top,
          width,
          height,
          defaultX: -bounds.left,
          defaultY: -bounds.top,
          index,
        });
      }
    }

    onStopDrag(event, data) {
      const { onStopDrag, index } = this.props;
      const { bounds, width, height } = this.state;
      this.setState({
        dragged: false,
        position: {
          x: 0,
          y: 0,
        },
      });
      if (onStopDrag) {
        onStopDrag({
          x: data.x - bounds.left,
          y: data.y - bounds.top,
          width,
          height,
          defaultX: -bounds.left,
          defaultY: -bounds.top,
          index,
        });
      }
    }

    render() {
      const { classes, ...otherProps } = this.props;
      const { bounds, position, dragged } = this.state;
      return (
        <Draggable
          axis="y"
          handle={`.${classes.draggableHandle}`}
          disabled={this.props.dragDisabled}
          {...bounds ? { bounds } : {}}
          {...position ? { position } : {}}
          onStart={this.onStartDrag}
          onDrag={this.onDrag}
          onStop={this.onStopDrag}
        >
          <div
            className={classNames(classes.draggable, {
              [classes.draggableDragged]: dragged,
            })}
            ref={(draggableRef) => {
              this.draggableRef = draggableRef;
            }}
          >
            <div
              className={classNames({
                [classes.draggableContentWithTransition]: this.props.withTransition,
              })}
              style={{ transform: `translateY(${this.props.offsetY}px)` }}
            >
              <WrappedComponent
                dragHandleClassName={classes.draggableHandle}
                dragged={dragged}
                {...otherProps}
              />
            </div>
          </div>
        </Draggable>
      );
    }
  }

  SortableListItem.propTypes = {
    boundsSelector: PropTypes.string,
    // dragHandleClassName: PropTypes.string.isRequired,
    dragDisabled: PropTypes.bool,
    offsetY: PropTypes.number,
    withTransition: PropTypes.bool,
    onStartDrag: PropTypes.func,
    onDrag: PropTypes.func,
    onStopDrag: PropTypes.func,
    onDraggableDataChange: PropTypes.func,
    index: PropTypes.number.isRequired,
    timestamp: PropTypes.number,
    classes: PropTypes.shape({
      draggable: PropTypes.string.isRequired,
      draggableDragged: PropTypes.string.isRequired,
      draggableContentWithTransition: PropTypes.string.isRequired,
      draggableHandle: PropTypes.string.isRequired,
    }).isRequired,
  };

  SortableListItem.defaultProps = {
    boundsSelector: null,
    dragDisabled: false,
    offsetY: 0,
    withTransition: false,
    onStartDrag: null,
    onDrag: null,
    onStopDrag: null,
    timestamp: null,
    onDraggableDataChange: () => {},
  };

  return withStyles(styles)(SortableListItem);
};

export default withSortableListItem;



// WEBPACK FOOTER //
// ./src/components/common/SortableList/withSortableListItem.js